import mixpanel from 'mixpanel-browser';
import { analyticsConfig, AnalyticsEvent, ErrorEvent, SearchEvent, PlaylistEvent, ShareEvent } from '../../config/analytics.config';
import { logger } from '../../utils/logger';

export class MixpanelAnalyticsService {
  private static instance: MixpanelAnalyticsService;
  private initialized = false;

  private constructor() {}

  static getInstance(): MixpanelAnalyticsService {
    if (!MixpanelAnalyticsService.instance) {
      MixpanelAnalyticsService.instance = new MixpanelAnalyticsService();
    }
    return MixpanelAnalyticsService.instance;
  }

  initialize(): void {
    // Always log initialization attempt in production for debugging
    const debugInfo = {
      initialized: this.initialized,
      enabled: analyticsConfig.mixpanel.enabled,
      hasToken: !!analyticsConfig.mixpanel.projectToken,
      tokenLength: analyticsConfig.mixpanel.projectToken?.length || 0
    };

    if (this.initialized || !analyticsConfig.mixpanel.enabled || !analyticsConfig.mixpanel.projectToken) {
      // Log to console even in production for debugging
      if (typeof window !== 'undefined' && window.location.search.includes('debug_analytics=true')) {
        console.log('⚠️ Mixpanel initialization skipped:', debugInfo);
      }
      logger.log('Mixpanel initialization skipped:', debugInfo);
      return;
    }

    try {
      logger.log('Initializing Mixpanel');
      mixpanel.init(analyticsConfig.mixpanel.projectToken, {
        debug: analyticsConfig.mixpanel.debug,
        track_pageview: false, // We'll handle page views manually
        persistence: 'localStorage',
        ip: !analyticsConfig.privacy.anonymizeIp // If anonymizeIp is true, don't track IP
      });

      this.initialized = true;
      logger.log('Mixpanel initialized successfully');
      // Log success in production with debug flag
      if (typeof window !== 'undefined' && window.location.search.includes('debug_analytics=true')) {
        console.log('✅ Mixpanel initialized successfully');
      }
    } catch (error) {
      logger.error('Failed to initialize Mixpanel:', error);
      // Log error in production with debug flag
      if (typeof window !== 'undefined' && window.location.search.includes('debug_analytics=true')) {
        console.error('❌ Mixpanel initialization failed:', error);
      }
    }
  }

  // Track page view
  trackPageView(path: string, title?: string): void {
    if (!this.initialized) return;

    mixpanel.track('Page View', {
      path,
      title: title || document.title,
      referrer: document.referrer
    });
  }

  // Generic event tracking
  trackEvent(event: AnalyticsEvent): void {
    if (!this.initialized) {
      logger.log('Mixpanel not initialized, skipping event:', event.name);
      return;
    }

    logger.log('Mixpanel tracking event:', event.name, event.parameters);
    mixpanel.track(event.name, {
      ...event.parameters,
      timestamp: event.timestamp || Date.now()
    });
  }

  // Track search events
  trackSearch(searchEvent: SearchEvent): void {
    this.trackEvent({
      name: 'Artist Search',
      parameters: {
        query: searchEvent.query,
        artist_name: searchEvent.artist,
        results_found: (searchEvent.resultsCount || 0) > 0,
        results_count: searchEvent.resultsCount,
        search_type: searchEvent.searchType,
        time_to_results_ms: searchEvent.timeToResults
      }
    });
  }

  // Track playlist events
  trackPlaylist(playlistEvent: PlaylistEvent): void {
    const eventName = {
      created: 'Playlist Created',
      saved: 'Playlist Saved',
      copied: 'Playlist Copied',
      played: 'Playlist Played'
    }[playlistEvent.action];

    this.trackEvent({
      name: eventName,
      parameters: {
        artist_name: playlistEvent.artistName,
        artist_id: playlistEvent.artistId,
        song_count: playlistEvent.songCount,
        platform: playlistEvent.platform,
        playlist_id: playlistEvent.playlistId
      }
    });
  }

  // Track share events
  trackShare(shareEvent: ShareEvent): void {
    this.trackEvent({
      name: 'Share',
      parameters: {
        method: shareEvent.method,
        artist_name: shareEvent.artistName,
        playlist_id: shareEvent.playlistId,
        success: shareEvent.success
      }
    });
  }

  // Track errors
  trackError(errorEvent: ErrorEvent): void {
    this.trackEvent({
      name: 'Error',
      parameters: {
        error_message: errorEvent.error.message,
        severity: errorEvent.severity,
        context: errorEvent.context
      }
    });
  }

  // Track playlist creation failure
  trackPlaylistCreationFailed(artistName: string, errorType: string): void {
    this.trackEvent({
      name: 'Playlist Creation Failed',
      parameters: {
        artist_name: artistName,
        error_type: errorType
      }
    });
  }
}

// Export singleton instance
export const mixpanelAnalytics = MixpanelAnalyticsService.getInstance();
